import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";

import { ConfirmAlert } from "../../../Alerts/Alerts";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { startGenerateCompetitorMyVersionMetered } from "../../../../actions/featureAddons";
import { useTranslationApp } from "../../../../lib/i18next";
import { useDispatchApp } from "../../../../lib/redux";
import { useRebrandBanner } from "../../../../hooks/useRebrandBanner";
import useInitialPreviewSelection from "../../../../hooks/useInitialPreviewSelection";
import {
  AI_COMPETITORS_RESULTS_GENERATE,
  BUTTON_SAVE,
  COMPETITOR_SCOPE_MODAL_TITLE,
  MODAL_COMPETITORS_ADD_OPTION,
  MODAL_COMPETITORS_CONFIRM_REFERENCE_DESCRIPTION,
  MODAL_COMPETITORS_CONFIRM_REFERENCE_TITLE,
  MODAL_COMPETITORS_FAVORITES_EMPTY,
  MODAL_COMPETITORS_PREPARING_REFERENCE,
  MODAL_COMPETITORS_SAVED_TO_FOLDER,
  MODAL_COMPETITORS_REMOVED_FROM_FOLDER,
  MODAL_COMPETITORS_SELECT_FOLDER_TITLE,
  MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT,
  FAVORITES_CATEGORY,
  GALLERY_FOLDERS_ALL,
  GALLERY_FOLDERS_MOVE,
} from "../../../../i18n/keysTranslations";
import {
  getReferenceAdStoragePath,
  ALL_FILTER,
} from "../../../../utils/constants";
import {
  getCommonFolderID,
  isCompetitorFavoritesFolder,
} from "../../../../utils/contentFolderUtils";
import useCompetitorsFolderSession from "../../../../hooks/useCompetitorsFolderSession";
import {
  COMPETITOR_FILTER_ADD_VALUE,
  COMPETITORS_LOADING_TIME_MS,
  MODAL_COMPETITORS_FROM_CREATE_WITH_IA,
  MODAL_COMPETITORS_FROM_MENU,
  MOVE_MODAL_PURPOSE_FAVORITE_DESTINATION,
  MOVE_MODAL_PURPOSE_SELECTION,
} from "../constants";
import {
  buildCompetitorFoldersForBar,
  getCompetitorFavoriteToastMedia,
  isCompetitorMediaAllowedForReference,
  getCompetitorFolderContentID,
  getCompetitorItemId,
  isValidCompetitorFavoriteDestination,
} from "../utils";
import useCompetitorsData from "./useCompetitorsData";
import useCompetitorsFilters from "./useCompetitorsFilters";
import useCompetitorsFavorites from "./useCompetitorsFavorites";
import useCompetitorFavoriteToast from "./useCompetitorFavoriteToast";
import useCompetitorsPreview from "./useCompetitorsPreview";
import useCompetitorsSelection from "./useCompetitorsSelection";

export const useModalCompetitorsController = ({
  modalOpen,
  onCloseModal,
  onCallbackSelectedItems,
  from = MODAL_COMPETITORS_FROM_MENU,
  initialPreviewLibraryID = null,
  onInitialPreviewLibraryIDConsumed = () => {},
  acceptedReferenceMediaType = null,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const pathStorageReferenceAd = getReferenceAdStoragePath(userID);
  const { totalTopOffsetPx } = useRebrandBanner();

  const isMenuMode = from === MODAL_COMPETITORS_FROM_MENU;
  const isCreateWithIAMode = from === MODAL_COMPETITORS_FROM_CREATE_WITH_IA;

  const [showEditCompetitors, setShowEditCompetitors] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const uploadingItemIdRef = useRef(null);
  const [folderFilterID, setFolderFilterID] = useState(ALL_FILTER);
  const [uiFolderID, setUiFolderID] = useState(ALL_FILTER);
  const [moveModalPurpose, setMoveModalPurpose] = useState(
    MOVE_MODAL_PURPOSE_SELECTION
  );
  const [, startGalleryTransition] = useTransition();
  const pendingFavoriteItemRef = useRef(null);
  const lastSavedFavoriteItemRef = useRef(null);
  const onChangeLocationRef = useRef(() => {});

  const onFolderFilterChange = useCallback((nextFolderID) => {
    setUiFolderID(nextFolderID);
    startGalleryTransition(() => {
      setFolderFilterID(nextFolderID);
    });
  }, []);

  const {
    isLoading,
    isLoadingContentReady,
    loadingDurationMs,
    onLoadingAnimationComplete,
    isUploading,
    isScrapingNewCompetitors,
    data,
    error,
    businessCompetitors,
    setBusinessCompetitors,
    onGetInitialData: fetchCompetitorsInitialData,
    onRefreshAfterCompetitorsSaved,
    onSendSelectedItems,
  } = useCompetitorsData({
    dispatch,
    pathStorageReferenceAd,
    modalOpen,
  });

  const { showFavoriteToast, favoriteToastProps } = useCompetitorFavoriteToast({
    modalOpen,
  });

  const {
    favoritesByPageId,
    isFavorite,
    saveToFavorites,
    removeFromFavorites,
    onAddFavorites,
    getFavoriteState,
    paywall: competitorsPaywall,
  } = useCompetitorsFavorites({
    modalOpen,
    dispatch,
  });

  const clearSelectionRef = useRef(() => {});

  const {
    folders,
    foldersByID,
    activeFolderID,
    folderMembershipByContentID,
    favoriteDestinationFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    onSelectFolder,
    onDropMany,
    moveContents,
    setFavoriteDestination,
    clearFavoriteDestination,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  } = useCompetitorsFolderSession({
    modalOpen,
    folderFilterID: uiFolderID,
    onFolderFilterChange,
    clearSelection: () => clearSelectionRef.current(),
  });

  const {
    control,
    platformFilterOptions,
    competitorFilterOptions,
    onPlatformFilterChange,
    galleryDisplayData,
    showFavoritesOnly,
    hasFavorites,
    resetFilters,
    resetCompetitorFilter,
    selectedCompetitorFilter,
    selectedPlatform,
  } = useCompetitorsFilters({
    data,
    businessCompetitors,
    favoritesByPageId,
    folderFilterID,
    folderMembershipByContentID,
    allowedPlatforms: competitorsPaywall.allowedPlatforms,
    visibilityLimit: competitorsPaywall.visibilityLimit,
  });

  const {
    selectedItemId,
    selectedGalleryIndexes,
    resetSelection,
    onSelectItem: onPickerSelectItem,
    getSelectedItems,
  } = useCompetitorsSelection({
    galleryDisplayData,
    acceptedReferenceMediaType,
  });

  clearSelectionRef.current = resetSelection;

  const {
    previewItem,
    isPreviewOpen,
    onOpenPreview,
    onClosePreview,
    resetPreview,
  } = useCompetitorsPreview({
    galleryDisplayData,
  });

  useInitialPreviewSelection({
    isOpen: modalOpen,
    isReady: !isLoading,
    previewId: initialPreviewLibraryID,
    items: galleryDisplayData,
    getId: (item) => item?.libraryID ?? item?.id,
    onSelect: onOpenPreview,
    onConsume: onInitialPreviewLibraryIDConsumed,
  });

  const resetInteractionState = useCallback(() => {
    resetSelection();
    resetPreview();
  }, [resetPreview, resetSelection]);

  const onGetInitialData = useCallback(async () => {
    resetInteractionState();
    await fetchCompetitorsInitialData();
  }, [fetchCompetitorsInitialData, resetInteractionState]);

  useEffect(() => {
    if (!modalOpen) return;
    onGetInitialData();
  }, [modalOpen, onGetInitialData]);

  useEffect(() => {
    if (modalOpen) return;
    resetPreview();
    resetFilters();
    setFolderFilterID(ALL_FILTER);
    setUiFolderID(ALL_FILTER);
    setShowEditCompetitors(false);
    setUploadingItemId(null);
    setMoveModalPurpose(MOVE_MODAL_PURPOSE_SELECTION);
    pendingFavoriteItemRef.current = null;
  }, [modalOpen, resetFilters, resetPreview]);

  useEffect(() => {
    resetInteractionState();
  }, [
    folderFilterID,
    selectedCompetitorFilter,
    selectedPlatform,
    resetInteractionState,
  ]);

  const processSelectedItems = useCallback(
    async (itemsToProcess) => {
      if (!itemsToProcess.length || isUploading) return;

      await onSendSelectedItems(itemsToProcess, {
        onCallbackSelectedItems,
        onCloseModal,
        allowVideo:
          !isCreateWithIAMode || acceptedReferenceMediaType === "video",
        acceptedReferenceMediaType,
      });
    },
    [
      acceptedReferenceMediaType,
      isCreateWithIAMode,
      isUploading,
      onCallbackSelectedItems,
      onCloseModal,
      onSendSelectedItems,
    ]
  );

  const onGenerateCompetitorItem = useCallback(
    async ({ item, itemId = null }) => {
      const isBlockedViewOnlyItem =
        isCreateWithIAMode &&
        !isCompetitorMediaAllowedForReference(
          item?.type,
          acceptedReferenceMediaType || "image"
        );
      if (
        !item?.id ||
        isBlockedViewOnlyItem ||
        isUploading ||
        uploadingItemIdRef.current
      ) {
        return;
      }

      const resolvedItemId = itemId ?? getCompetitorItemId(item);
      uploadingItemIdRef.current = resolvedItemId || "pending";
      if (resolvedItemId) setUploadingItemId(resolvedItemId);

      const generateHeld = competitorsPaywall.eligible;
      if (
        await competitorsPaywall.blockIfGenerateLocked({
          censored: item.featureAddonCensored,
        })
      ) {
        uploadingItemIdRef.current = null;
        setUploadingItemId(null);
        return;
      }

      if (isCreateWithIAMode) {
        const response = await ConfirmAlert({
          title: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_TITLE),
          text: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_DESCRIPTION),
          showCancelButton: true,
        });
        if (!response.isConfirmed) {
          if (generateHeld) competitorsPaywall.releasePendingAction("generate");
          uploadingItemIdRef.current = null;
          setUploadingItemId(null);
          return;
        }
      }

      if (generateHeld) {
        dispatch(
          startGenerateCompetitorMyVersionMetered({
            pageId: item.pageId,
            libraryID: item.libraryID ?? item.id,
          })
        ).finally(() => competitorsPaywall.releasePendingAction("generate"));
      }

      try {
        await processSelectedItems([item]);
      } finally {
        uploadingItemIdRef.current = null;
        if (resolvedItemId) setUploadingItemId(null);
        if (isCreateWithIAMode) {
          resetSelection();
        }
      }
    },
    [
      acceptedReferenceMediaType,
      competitorsPaywall,
      dispatch,
      isCreateWithIAMode,
      isUploading,
      processSelectedItems,
      resetSelection,
      t,
    ]
  );

  const onDirectGenerateItem = useCallback(
    async ({ index, url, type }) => {
      const items = getSelectedItems([index]);
      if (!items.length) return;

      const item = items[0];
      const resolvedItem =
        url != null
          ? {
              ...item,
              url,
              ...(type ? { type } : {}),
            }
          : item;

      await onGenerateCompetitorItem({
        item: resolvedItem,
        itemId: getCompetitorItemId(item),
      });
    },
    [getSelectedItems, onGenerateCompetitorItem]
  );

  const onSelectItemWithConfirm = useCallback(
    async (index, mediaOverride) => {
      const item = galleryDisplayData[index];
      const selectedType = mediaOverride?.type || item?.type;
      const selectedUrl = mediaOverride?.url || item?.url;
      if (
        !item?.id ||
        !isCompetitorMediaAllowedForReference(
          selectedType,
          acceptedReferenceMediaType || "image"
        ) ||
        isUploading
      ) {
        return;
      }

      const itemId = getCompetitorItemId(item);
      const wasSelected = selectedItemId === itemId;
      onPickerSelectItem(index, mediaOverride);

      if (wasSelected) return;

      const response = await ConfirmAlert({
        title: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_TITLE),
        text: t(MODAL_COMPETITORS_CONFIRM_REFERENCE_DESCRIPTION),
        showCancelButton: true,
      });

      if (!response.isConfirmed) {
        onPickerSelectItem(index, mediaOverride);
        return;
      }

      setUploadingItemId(itemId);
      try {
        await processSelectedItems([
          {
            ...item,
            url: selectedUrl,
            type: selectedType === "video" ? "video" : "image",
          },
        ]);
      } finally {
        setUploadingItemId(null);
        resetSelection();
      }
    },
    [
      acceptedReferenceMediaType,
      galleryDisplayData,
      isUploading,
      onPickerSelectItem,
      processSelectedItems,
      resetSelection,
      selectedItemId,
      t,
    ]
  );

  const onSelectItem = isCreateWithIAMode
    ? onSelectItemWithConfirm
    : onPickerSelectItem;

  const onSelectPreview = useCallback(
    (index) => {
      onOpenPreview(index);
    },
    [onOpenPreview]
  );

  const onOpenEditCompetitors = useCallback(() => {
    setShowEditCompetitors(true);
    resetInteractionState();
  }, [resetInteractionState]);

  const onCloseEditCompetitors = useCallback(() => {
    setShowEditCompetitors(false);
  }, []);

  const onCompetitorsSaved = useCallback(
    async ({ savedCompetitors }) => {
      const previousCompetitors = businessCompetitors;
      setBusinessCompetitors(savedCompetitors);
      setShowEditCompetitors(false);
      resetCompetitorFilter();
      resetInteractionState();
      await onRefreshAfterCompetitorsSaved({
        savedCompetitors,
        previousCompetitors,
      });
    },
    [
      businessCompetitors,
      onRefreshAfterCompetitorsSaved,
      resetCompetitorFilter,
      resetInteractionState,
      setBusinessCompetitors,
    ]
  );

  const onCompetitorFilterValidationChange = useCallback(
    async (value) => {
      if (value === COMPETITOR_FILTER_ADD_VALUE) {
        onOpenEditCompetitors();
        return false;
      }
      return true;
    },
    [onOpenEditCompetitors]
  );

  const foldersForBar = useMemo(
    () =>
      buildCompetitorFoldersForBar({
        folders,
        favoritesLabel: t(FAVORITES_CATEGORY),
      }),
    [folders, t]
  );

  const validFavoriteDestinationFolderID = useMemo(() => {
    if (!favoriteDestinationFolderID) return null;

    return isValidCompetitorFavoriteDestination({
      folderID: favoriteDestinationFolderID,
      foldersByID,
    })
      ? favoriteDestinationFolderID
      : null;
  }, [favoriteDestinationFolderID, foldersByID]);

  useEffect(() => {
    if (!modalOpen || !favoriteDestinationFolderID) return;
    if (validFavoriteDestinationFolderID) return;

    clearFavoriteDestination();
  }, [
    clearFavoriteDestination,
    favoriteDestinationFolderID,
    modalOpen,
    validFavoriteDestinationFolderID,
  ]);

  const getDestinationFolderLabel = useCallback(
    ({ folderID }) => {
      if (isCompetitorFavoritesFolder(folderID)) {
        return t(FAVORITES_CATEGORY);
      }

      return getFolderLabel({ folderID });
    },
    [getFolderLabel, t]
  );

  const showSavedToDestinationToast = useCallback(
    ({ item, folderID }) => {
      if (!item) return;

      const { url: mediaUrl, type: mediaType } =
        getCompetitorFavoriteToastMedia(item);

      showFavoriteToast({
        mediaUrl,
        mediaType,
        messageKey: MODAL_COMPETITORS_SAVED_TO_FOLDER,
        messageParams: {
          folder: getDestinationFolderLabel({ folderID }),
        },
        showChangeLocation: true,
        onChangeLocation: onChangeLocationRef.current,
      });
    },
    [getDestinationFolderLabel, showFavoriteToast]
  );

  const showRemovedFromDestinationToast = useCallback(
    ({ item, folderID }) => {
      if (!item) return;

      const { url: mediaUrl, type: mediaType } =
        getCompetitorFavoriteToastMedia(item);

      showFavoriteToast({
        mediaUrl,
        mediaType,
        messageKey: MODAL_COMPETITORS_REMOVED_FROM_FOLDER,
        messageParams: {
          folder: getDestinationFolderLabel({ folderID }),
        },
      });
    },
    [getDestinationFolderLabel, showFavoriteToast]
  );

  const isItemSavedAtDestination = useCallback(
    ({ item, folderID }) => {
      const contentID = getCompetitorFolderContentID(item);
      if (!folderID) return false;

      if (isCompetitorFavoritesFolder(folderID)) {
        return isFavorite({
          pageId: item?.pageId,
          libraryID: item?.libraryID ?? item?.id,
          item,
        });
      }

      if (!contentID) return false;

      return folderMembershipByContentID[contentID] === folderID;
    },
    [folderMembershipByContentID, isFavorite]
  );

  const saveItemToDestination = useCallback(
    async ({ item, folderID }) => {
      const contentID = getCompetitorFolderContentID(item);
      if (!item?.pageId || !contentID || !folderID) {
        return { ok: false };
      }

      if (isCompetitorFavoritesFolder(folderID)) {
        if (folderMembershipByContentID[contentID]) {
          const unassignResponse = await moveContents({
            contentIDs: [contentID],
            folderID: null,
          });

          if (
            !unassignResponse?.ok &&
            unassignResponse?.reason !== "noChanges"
          ) {
            return unassignResponse;
          }
        }

        const saveResponse = await saveToFavorites({ item });
        if (!saveResponse?.ok) return saveResponse;
      } else {
        const removeResponse = await removeFromFavorites({ item });
        if (!removeResponse?.ok) return removeResponse;

        const moveResponse = await moveContents({
          contentIDs: [contentID],
          folderID,
        });

        if (!moveResponse?.ok && moveResponse?.reason !== "noChanges") {
          return moveResponse;
        }
      }

      lastSavedFavoriteItemRef.current = item;
      showSavedToDestinationToast({ item, folderID });
      return { ok: true };
    },
    [
      folderMembershipByContentID,
      moveContents,
      removeFromFavorites,
      saveToFavorites,
      showSavedToDestinationToast,
    ]
  );

  const unsaveItemFromDestination = useCallback(
    async ({ item, folderID }) => {
      const contentID = getCompetitorFolderContentID(item);
      if (!folderID) return;

      if (isCompetitorFavoritesFolder(folderID)) {
        const removeResponse = await removeFromFavorites({ item });
        if (!removeResponse?.ok) return;

        showRemovedFromDestinationToast({ item, folderID });
        return;
      }

      if (!contentID) return;

      const moveResponse = await moveContents({
        contentIDs: [contentID],
        folderID: null,
      });

      if (!moveResponse?.ok && moveResponse?.reason !== "noChanges") {
        return;
      }

      showRemovedFromDestinationToast({ item, folderID });
    },
    [moveContents, removeFromFavorites, showRemovedFromDestinationToast]
  );

  const onCloseMoveFolderModalWithReset = useCallback(() => {
    closeMoveFolderModal();
    pendingFavoriteItemRef.current = null;
  }, [closeMoveFolderModal]);

  const openFavoriteDestinationModal = useCallback(
    ({ item }) => {
      pendingFavoriteItemRef.current = item;
      setMoveModalPurpose(MOVE_MODAL_PURPOSE_FAVORITE_DESTINATION);
      openMoveFolderModal();
    },
    [openMoveFolderModal]
  );

  const onOpenSelectionMoveFolderModal = useCallback(() => {
    setMoveModalPurpose(MOVE_MODAL_PURPOSE_SELECTION);
    openMoveFolderModal();
  }, [openMoveFolderModal]);

  onChangeLocationRef.current = () => {
    const item = lastSavedFavoriteItemRef.current;
    if (item) {
      openFavoriteDestinationModal({ item });
    }
  };

  const onToggleCompetitorFavoriteItem = useCallback(
    async ({ item }) => {
      if (!item?.url) return;

      const destinationFolderID = validFavoriteDestinationFolderID;

      if (
        destinationFolderID &&
        isItemSavedAtDestination({ item, folderID: destinationFolderID })
      ) {
        await unsaveItemFromDestination({
          item,
          folderID: destinationFolderID,
        });
        return;
      }

      if (!destinationFolderID) {
        openFavoriteDestinationModal({ item });
        return;
      }

      await saveItemToDestination({ item, folderID: destinationFolderID });
    },
    [
      isItemSavedAtDestination,
      openFavoriteDestinationModal,
      saveItemToDestination,
      unsaveItemFromDestination,
      validFavoriteDestinationFolderID,
    ]
  );

  const getCompetitorFavoriteState = useCallback(
    (item) => {
      const destinationFolderID = validFavoriteDestinationFolderID;

      if (
        destinationFolderID &&
        !isCompetitorFavoritesFolder(destinationFolderID)
      ) {
        const contentID = getCompetitorFolderContentID(item);

        return {
          isFavorite:
            folderMembershipByContentID[contentID] === destinationFolderID,
          isLoading: false,
        };
      }

      return getFavoriteState(item);
    },
    [
      folderMembershipByContentID,
      getFavoriteState,
      validFavoriteDestinationFolderID,
    ]
  );

  const onPreviewGenerate = useCallback(() => {
    if (!previewItem) return;
    onGenerateCompetitorItem({ item: previewItem });
  }, [onGenerateCompetitorItem, previewItem]);

  const selectedGalleryItems = useMemo(
    () => getSelectedItems(selectedGalleryIndexes),
    [getSelectedItems, selectedGalleryIndexes]
  );

  const selectedContentIDs = useMemo(
    () =>
      selectedGalleryItems
        .map((item) => getCompetitorFolderContentID(item))
        .filter(Boolean),
    [selectedGalleryItems]
  );

  const itemsByContentID = useMemo(() => {
    const map = new Map();

    (data || []).forEach((item) => {
      const contentID = getCompetitorFolderContentID(item);
      if (contentID) map.set(contentID, item);
    });

    return map;
  }, [data]);

  const onDropManyWithFavorites = useCallback(
    async ({ contentIDs, folderID }) => {
      if (isCompetitorFavoritesFolder(folderID)) {
        const items = contentIDs
          .map((contentID) => itemsByContentID.get(contentID))
          .filter(Boolean);
        onAddFavorites({ items });
        return;
      }

      await onDropMany({ contentIDs, folderID });
    },
    [itemsByContentID, onAddFavorites, onDropMany]
  );

  const disabledMoveFolderID = useMemo(
    () => getCommonFolderID({ items: selectedGalleryItems }),
    [selectedGalleryItems]
  );

  const currentFolderLabelForMove = useMemo(() => {
    if (isCompetitorFavoritesFolder(activeFolderID)) {
      return t(FAVORITES_CATEGORY);
    }
    if (activeFolderID) {
      return getFolderLabel({ folderID: activeFolderID });
    }
    return t(GALLERY_FOLDERS_ALL);
  }, [activeFolderID, getFolderLabel, t]);

  const onCallbackMoveToFolder = useCallback(
    async ({ folderID }) => {
      if (moveModalPurpose === MOVE_MODAL_PURPOSE_FAVORITE_DESTINATION) {
        const item = pendingFavoriteItemRef.current;
        if (!item) return { ok: false };

        const previousDestination = validFavoriteDestinationFolderID;
        const setDestinationResponse = await setFavoriteDestination({
          folderID,
        });

        if (!setDestinationResponse?.ok) {
          return setDestinationResponse;
        }

        const saveResponse = await saveItemToDestination({ item, folderID });

        if (!saveResponse?.ok) {
          if (previousDestination) {
            await setFavoriteDestination({ folderID: previousDestination });
          } else {
            await clearFavoriteDestination();
          }
          return saveResponse;
        }

        onCloseMoveFolderModalWithReset();
        return { ok: true };
      }

      const response = await moveContents({
        contentIDs: selectedContentIDs,
        folderID,
      });

      if (!response?.ok) return response;

      closeMoveFolderModal();
      return response;
    },
    [
      clearFavoriteDestination,
      closeMoveFolderModal,
      moveContents,
      moveModalPurpose,
      onCloseMoveFolderModalWithReset,
      saveItemToDestination,
      selectedContentIDs,
      setFavoriteDestination,
      validFavoriteDestinationFolderID,
    ]
  );

  const isFavoriteDestinationMoveModal =
    moveModalPurpose === MOVE_MODAL_PURPOSE_FAVORITE_DESTINATION;

  const emptyContentKeyOverride =
    showFavoritesOnly && galleryDisplayData.length === 0
      ? MODAL_COMPETITORS_FAVORITES_EMPTY
      : activeFolderID &&
          !isCompetitorFavoritesFolder(activeFolderID) &&
          galleryDisplayData.length === 0
        ? MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT
        : null;

  const showCompetitorFilter =
    !isLoading &&
    !error &&
    (data.length > 0 || hasFavorites || businessCompetitors.length > 0);

  const showFolderBar = showCompetitorFilter;

  const layoutProps = useMemo(
    () => ({
      modalOpen,
      onCloseModal,
      title: t(COMPETITOR_SCOPE_MODAL_TITLE),
      isLoading,
      isLoadingContentReady,
      loadingDurationMs,
      onLoadingAnimationComplete,
      error,
      totalTopOffsetPx,
      emptyContentKey: MODAL_GALLERY_META_AD_LIBRARY_EMPTY_CONTENT,
      emptyContentKeyOverride,
      showCompetitorFilter,
      showFolderBar,
      isUploading,
      isScrapingNewCompetitors,
      scrapingDurationMs: COMPETITORS_LOADING_TIME_MS,
      showUploadingFooter: isCreateWithIAMode,
      uploadingFooterLabels: [MODAL_COMPETITORS_PREPARING_REFERENCE],
    }),
    [
      emptyContentKeyOverride,
      error,
      isCreateWithIAMode,
      isLoading,
      isLoadingContentReady,
      isScrapingNewCompetitors,
      isUploading,
      loadingDurationMs,
      modalOpen,
      onCloseModal,
      onLoadingAnimationComplete,
      showCompetitorFilter,
      showFolderBar,
      t,
      totalTopOffsetPx,
    ]
  );

  const filtersProps = useMemo(
    () => ({
      control,
      platformFilterOptions,
      competitorFilterOptions,
      onPlatformFilterChange,
      onCompetitorFilterValidationChange,
      addOptionLabel: t(MODAL_COMPETITORS_ADD_OPTION),
    }),
    [
      competitorFilterOptions,
      control,
      onCompetitorFilterValidationChange,
      onPlatformFilterChange,
      platformFilterOptions,
      t,
    ]
  );

  const folderBarProps = useMemo(
    () => ({
      folders: foldersForBar,
      activeFolderID,
      selectedContentIDs,
      canEdit: true,
      isLoading: isLoadingFolders || isCreatingFolder || isMovingContents,
      onSelectFolder,
      onOpenCreateFolder: openCreateFolderModal,
      onDropMany: onDropManyWithFavorites,
      onRenameFolder: openRenameFolderModal,
      onDeleteFolder,
      showMoveFolderButton: isCreateWithIAMode && selectedContentIDs.length > 0,
      moveFolderLabel: t(GALLERY_FOLDERS_MOVE),
      onOpenMoveFolderModal: onOpenSelectionMoveFolderModal,
    }),
    [
      activeFolderID,
      foldersForBar,
      isCreateWithIAMode,
      isCreatingFolder,
      isLoadingFolders,
      isMovingContents,
      onDeleteFolder,
      onDropManyWithFavorites,
      onOpenSelectionMoveFolderModal,
      onSelectFolder,
      openCreateFolderModal,
      openRenameFolderModal,
      selectedContentIDs,
      t,
    ]
  );

  const galleryProps = useMemo(
    () => ({
      items: galleryDisplayData,
      isMenuMode,
      isCreateWithIAMode,
      acceptedReferenceMediaType,
      selectedIndexes: selectedGalleryIndexes,
      selectedContentIDs,
      draggableForFolders: true,
      uploadingItemId,
      directAddLabel: t(AI_COMPETITORS_RESULTS_GENERATE),
      isUploading,
      onSelectItem,
      onSelectPreview,
      onDirectGenerateItem,
      onToggleFavoriteItem: onToggleCompetitorFavoriteItem,
      onCensoredItem: () => competitorsPaywall.openPaywall(),
      getFavoriteState: getCompetitorFavoriteState,
    }),
    [
      acceptedReferenceMediaType,
      galleryDisplayData,
      getCompetitorFavoriteState,
      isCreateWithIAMode,
      isMenuMode,
      isUploading,
      onDirectGenerateItem,
      onSelectItem,
      onSelectPreview,
      onToggleCompetitorFavoriteItem,
      competitorsPaywall,
      selectedContentIDs,
      selectedGalleryIndexes,
      t,
      uploadingItemId,
    ]
  );

  const folderModalsProps = useMemo(
    () => ({
      folders: isFavoriteDestinationMoveModal ? foldersForBar : folders,
      isOpenCreateFolderModal,
      closeCreateFolderModal,
      createFolderParentID,
      createFolderParentLabel,
      isCreatingFolder,
      onCreateFolderInContext,
      isOpenMoveFolderModal,
      closeMoveFolderModal: isFavoriteDestinationMoveModal
        ? onCloseMoveFolderModalWithReset
        : closeMoveFolderModal,
      currentFolderLabelForMove,
      disabledMoveFolderID: isFavoriteDestinationMoveModal
        ? null
        : disabledMoveFolderID,
      isMovingContents,
      onCallbackMoveToFolder,
      showMoveResultAlerts: !isFavoriteDestinationMoveModal,
      moveModalTitleKey: isFavoriteDestinationMoveModal
        ? MODAL_COMPETITORS_SELECT_FOLDER_TITLE
        : undefined,
      moveModalPrimaryButtonKey: isFavoriteDestinationMoveModal
        ? BUTTON_SAVE
        : undefined,
      showMoveDescription: !isFavoriteDestinationMoveModal,
      isOpenRenameFolderModal,
      closeRenameFolderModal,
      renameFolderTarget,
      isRenamingFolder,
      onRenameFolder,
    }),
    [
      closeCreateFolderModal,
      closeMoveFolderModal,
      closeRenameFolderModal,
      createFolderParentID,
      createFolderParentLabel,
      currentFolderLabelForMove,
      disabledMoveFolderID,
      folders,
      foldersForBar,
      isCreatingFolder,
      isFavoriteDestinationMoveModal,
      isMovingContents,
      isOpenCreateFolderModal,
      isOpenMoveFolderModal,
      isOpenRenameFolderModal,
      isRenamingFolder,
      onCallbackMoveToFolder,
      onCloseMoveFolderModalWithReset,
      onCreateFolderInContext,
      onRenameFolder,
      renameFolderTarget,
    ]
  );

  const previewProps = useMemo(
    () => ({
      modalOpen: isPreviewOpen,
      item: previewItem,
      onCloseModal: onClosePreview,
      onGenerate: onPreviewGenerate,
      isUploading,
      generateLabel: t(AI_COMPETITORS_RESULTS_GENERATE),
      acceptedReferenceMediaType,
    }),
    [
      acceptedReferenceMediaType,
      isPreviewOpen,
      isUploading,
      onClosePreview,
      onPreviewGenerate,
      previewItem,
      t,
    ]
  );

  const editorProps = useMemo(
    () => ({
      modalOpen: showEditCompetitors,
      onCloseModal: onCloseEditCompetitors,
      businessCompetitors,
      dispatch,
      onSaved: onCompetitorsSaved,
    }),
    [
      businessCompetitors,
      dispatch,
      onCloseEditCompetitors,
      onCompetitorsSaved,
      showEditCompetitors,
    ]
  );

  return {
    layoutProps: {
      ...layoutProps,
      folderBarProps,
      favoriteToastProps,
      competitorsPaywall,
    },
    filtersProps,
    galleryProps,
    previewProps,
    editorProps,
    folderModalsProps,
  };
};

export default useModalCompetitorsController;
